import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserSettingsService } from '../../../../services/members/user-settings.service';
import { User } from '../../../../models/entities/user';

/**
 * The message core's TwoFactorAlreadyConfiguredException renders (SD-10440) when a setup start or
 * finish arrives for a method the account already has: a double-submit, a retried request, or a
 * stepper left open in a second tab. Core guards each method on its own column, so this never fires
 * when a user with one method adds the other.
 *
 * It is a contract with core — rewording either side silently turns the neutral "already enabled"
 * notice back into a red error. The literal is pinned on BOTH sides (two-factor-already-enabled.spec.ts
 * here, TwoFactorAlreadyConfiguredTest in core), so a reword fails a test that names the other file.
 */
export const TWO_FACTOR_ALREADY_ENABLED_MESSAGE = 'This two-factor method is already enabled on your account.';

export const TWO_FACTOR_ALREADY_ENABLED_COPY: {title: string; message: string} = {
    title: 'Already enabled',
    message: TWO_FACTOR_ALREADY_ENABLED_MESSAGE,
};

/** True when a setup request was refused only because the method is already enrolled. */
export function isTwoFactorAlreadyEnabled(response: unknown): boolean {
    return response instanceof HttpErrorResponse
        && response.status === 400
        && response.error?.message === TWO_FACTOR_ALREADY_ENABLED_MESSAGE;
}

export type TwoFactorSetupMethod = 'sms' | 'google';

/**
 * Re-reads your own user record from the server and answers whether THIS method is enrolled (review
 * F-f7cf). The "already enabled" 400 alone is not trusted to close the stepper: the host's own view may
 * be stale (that is how a second tab gets here), so the server's current enrollment decides. Any doubt —
 * the read failing — answers false, which keeps the original error. Reads the user record rather than
 * the member, which the super 2FA gate refuses to an un-enrolled super (SD-10091).
 */
export function isMethodEnrolled(userSettings: UserSettingsService, method: TwoFactorSetupMethod): Observable<boolean> {
    return userSettings.getOwnUser().pipe(
        map((user: User) => method === 'sms'
            ? !!user?.isSmsAuthVerificationEnrolled
            : !!user?.isGoogleAuthVerificationEnrolled),
        catchError(() => of(false)),
    );
}
